import { createAsyncThunk } from "@reduxjs/toolkit";

import { RootState } from "../../../app/store";
import { AccountOverviewsAPI, GetAccountOverviewsResponse } from "../../../api/account_overviews";
import { CreditAccountDetailsAPI, UpdateCreditAccountDetailsRequest } from "../../../api/credit_account_details";
import { CreditAccountCurrentPositionsAPI, UpsertCreditAccountCurrentPositionRequest } from "../../../api/credit_account_current_positions";
import { CreditAccountProvisionsAPI, UpsertCreditAccountProvisionRequest } from "../../../api/credit_account_provisions";
import { CreditAccountRecoveryAPI, UpsertCreditAccountRecoveryRequest } from "../../../api/credit_account_recovery";
import { MessageResponse } from "../../../api/common";

export const fetchAccountOverviews = createAsyncThunk<
  GetAccountOverviewsResponse,
  string,
  {
    state: RootState;
    rejectValue: string;
  }
>(
  "accountDetails/fetchAccountOverviews",
  async (caseId, { rejectWithValue }) => {
    try {
      return await AccountOverviewsAPI.get(caseId);
    } catch {
      return rejectWithValue("Failed to fetch account overviews");
    }
  },
  {
    condition: (caseId, { getState }) => {
      const state = getState() as RootState;
      return state.creditAccounts.caseId !== caseId;
    },
  },
);

export interface UpdateCreditAccountDetailsParams {
  caseId: string;
  accountNumber: string;
  request: UpdateCreditAccountDetailsRequest;
}

export const updateCreditAccountDetails = createAsyncThunk<
  MessageResponse,
  UpdateCreditAccountDetailsParams,
  { rejectValue: string }
>(
  "accountDetails/updateCreditAccountDetails",
  async ({ caseId, accountNumber, request }, { rejectWithValue }) => {
    try {
      return await CreditAccountDetailsAPI.update(caseId, accountNumber, request);
    } catch {
      return rejectWithValue("Failed to update credit account details");
    }
  },
);

export interface UpsertCreditAccountCurrentPositionParams {
  caseId: string;
  accountNumber: string;
  request: UpsertCreditAccountCurrentPositionRequest;
}

export const upsertCreditAccountCurrentPosition = createAsyncThunk<
  MessageResponse,
  UpsertCreditAccountCurrentPositionParams,
  { rejectValue: string }
>(
  "accountDetails/upsertCreditAccountCurrentPosition",
  async ({ caseId, accountNumber, request }, { rejectWithValue }) => {
    try {
      return await CreditAccountCurrentPositionsAPI.upsert(
        caseId,
        accountNumber,
        request,
      );
    } catch {
      return rejectWithValue("Failed to update current position");
    }
  },
);

export interface UpsertCreditAccountProvisionParams {
  caseId: string;
  accountNumber: string;
  request: UpsertCreditAccountProvisionRequest;
}

export const upsertCreditAccountProvision = createAsyncThunk<
  MessageResponse,
  UpsertCreditAccountProvisionParams,
  { rejectValue: string }
>(
  "accountDetails/upsertCreditAccountProvision",
  async ({ caseId, accountNumber, request }, { rejectWithValue }) => {
    try {
      return await CreditAccountProvisionsAPI.upsert(
        caseId,
        accountNumber,
        request,
      );
    } catch {
      return rejectWithValue("Failed to update account provision");
    }
  },
);

export interface UpsertCreditAccountRecoveryParams {
  caseId: string;
  accountNumber: string;
  request: UpsertCreditAccountRecoveryRequest;
}

export const upsertCreditAccountRecovery = createAsyncThunk<
  MessageResponse,
  UpsertCreditAccountRecoveryParams,
  { rejectValue: string }
>(
  "accountDetails/upsertCreditAccountRecovery",
  async ({ caseId, accountNumber, request }, { rejectWithValue }) => {
    try {
      return await CreditAccountRecoveryAPI.upsert(
        caseId,
        accountNumber,
        request,
      );
    } catch {
      return rejectWithValue("Failed to update account recovery");
    }
  },
);